import classNames from 'classnames';
import PropTypes from 'prop-types';
import { parse as parseQuery } from 'query-string';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import useLoginComplete from '../../hooks/useLoginComplete';
import useScrollToTop from '../../hooks/useScrollToTop';

import { useUser } from '../../contexts/AuthContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import LoginForm from '../forms/Login';
import RegisterForm from '../forms/Register';
import MainLayout from '../layouts/Main';
import FormFrame from '../partials/FormFrame';
import Link from '../partials/Link';
import NotificationPanel from '../partials/NotificationPanel';

import styles from '../../../styles/pages/form.module.scss';

const propTypes = {
    className: PropTypes.string,
    withoutRegister: PropTypes.bool,
};

const defaultProps = {
    className: null,
    withoutRegister: false,
};

const LoginPage = ({ className, withoutRegister }) => {
    const onLoginComplete = useLoginComplete();
    const location = useLocation();
    const route = useUrlGenerator();
    const user = useUser();
    const { search } = location;

    const { tickets, email, favorites, changed } = useMemo(() => {
        const query = parseQuery(search);
        const {
            tickets: queryTickets = null,
            changed: queryChanged = null,
            favorites: queryFavorites = null,
            email: queryEmail = null,
        } = query || {};
        return {
            tickets: parseInt(queryTickets, 10) === 1,
            favorites: parseInt(queryFavorites, 10) === 1,
            changed: parseInt(queryChanged, 10) === 1,
            email: parseInt(queryEmail, 10) === 1,
        };
    }, [search]);

    useScrollToTop(location, search);

    return (
        <MainLayout>
            <div
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                        [styles.withColumns]: !withoutRegister,
                    },
                ])}
            >
                <div className={styles.notifications}>
                    {tickets ? (
                        <NotificationPanel
                            rounded
                            label={
                                <FormattedMessage
                                    defaultMessage="To make purchases you must login or create an account."
                                    description="Message"
                                />
                            }
                        />
                    ) : null}
                    {favorites ? (
                        <NotificationPanel
                            rounded
                            label={
                                <FormattedMessage
                                    defaultMessage="To add favorites and events to your calendar you must login or create an account."
                                    description="Message"
                                />
                            }
                        />
                    ) : null}
                    {email ? (
                        <NotificationPanel
                            rounded
                            label={
                                <FormattedMessage
                                    defaultMessage="To rent online movies you must verify your email address. Check your inbox and click on the link inside to confirm."
                                    description="Message"
                                />
                            }
                        />
                    ) : null}
                    {changed ? (
                        <NotificationPanel
                            rounded
                            label={
                                <FormattedMessage
                                    defaultMessage="Your password was successfully changed. Please login with your new password to continue."
                                    description="Message"
                                />
                            }
                        />
                    ) : null}
                </div>
                <div className={styles.inner}>
                    <FormFrame
                        className={styles.formFrame}
                        title={
                            <FormattedMessage defaultMessage="Login" description="Form heading" />
                        }
                    >
                        {user === null ? (
                            <LoginForm onComplete={onLoginComplete} />
                        ) : (
                            <div>
                                <Link to={route('account')}>
                                    <FormattedMessage
                                        defaultMessage="Go to my account"
                                        description="Form heading"
                                    />
                                </Link>
                            </div>
                        )}
                    </FormFrame>
                    {!withoutRegister && (
                        <FormFrame
                            className={styles.formFrame}
                            title={
                                <FormattedMessage
                                    defaultMessage="Create an account"
                                    description="Form heading"
                                />
                            }
                        >
                            <RegisterForm onComplete={onLoginComplete} />
                        </FormFrame>
                    )}
                </div>
            </div>
        </MainLayout>
    );
};
LoginPage.propTypes = propTypes;
LoginPage.defaultProps = defaultProps;

export default LoginPage;
